import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ApiError, apiClient } from 'shared/api';
import { useInfoStore } from 'store/useInfoStore';

export const welcomeCouponQueryKey = ['member-welcome-coupon'];

export type WelcomeCouponResponse = {
  eligible: boolean;
  coupon: { code: string; issuedAt: string } | null;
};

export const getWelcomeCoupon = async (): Promise<WelcomeCouponResponse> => {
  const { data } = await apiClient.get('/coupons/welcome');
  if (!data.isSuccess) throw new ApiError(data);
  return data.result;
};

export const useDeclineWelcomeCoupon = () => {
  const client = useQueryClient();
  const userId = useInfoStore((state) => state.userId);
  return useMutation({
    mutationFn: async () => {
      const { data } = await apiClient.post('/coupons/welcome/decline');
      if (!data.isSuccess) throw new ApiError(data);
    },
    onSuccess: async () => {
      await client.cancelQueries({ queryKey: [...welcomeCouponQueryKey, userId] });
      client.setQueryData<WelcomeCouponResponse>([...welcomeCouponQueryKey, userId], (current) => ({
        eligible: false,
        coupon: current?.coupon ?? null,
      }));
      await client.invalidateQueries({ queryKey: [...welcomeCouponQueryKey, userId] });
    },
  });
};

/** 회원별 서버 발급내역만 신뢰한다. 미배포 API·오류·로그아웃에서는 쿠폰을 노출하지 않는다. */
export const useWelcomeCoupon = () => {
  const { isLogin, userId } = useInfoStore();
  const query = useQuery({
    queryKey: [...welcomeCouponQueryKey, userId],
    queryFn: getWelcomeCoupon,
    enabled: isLogin && !!userId,
    retry: false,
    throwOnError: false,
    gcTime: 0,
  });
  const data = isLogin && query.isSuccess ? query.data : undefined;
  return { coupon: data?.coupon ?? null, eligible: data?.eligible === true };
};
